<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { formatAddress, isAddressComplete } from '@/helpers/format';
import { TrashIcon } from '@heroicons/vue/24/outline';

import PropertyDetailForm from './PropertyDetailForm.vue';
import type { ErhlPropertyMode } from './PropertyDetailForm.vue';
import type { PropertyDetailEntry } from '@/types/Property';

const props = withDefaults(
  defineProps<{
    modelValue: PropertyDetailEntry;
    propertyMode: ErhlPropertyMode;
    isMultiple?: boolean;
    index?: number;
    removable?: boolean;
    startEditing?: boolean;
  }>(),
  {
    isMultiple: false,
    index: 1,
    removable: true,
    startEditing: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: PropertyDetailEntry): void;
  (e: 'remove', id: string): void;
  (e: 'saved'): void;
}>();

const inputVal = ref<PropertyDetailEntry>(props.modelValue);
const newItem = ref(!props.modelValue.address);
const showEntry = ref(!isAddressComplete(props.modelValue.address));
const propertyRef = ref<InstanceType<typeof PropertyDetailForm>>();
const showModal = ref(false);

const address = computed(() =>
  props.modelValue.address ? formatAddress(props.modelValue.address) : 'the new property',
);

const propertyIcon = computed(() => {
  switch (props.propertyMode) {
    case 'purchase':
      return 'incomingProperty';
    case 'supporting':
    default:
      return 'supportingProperty';
  }
});

/**
 * Returns the section header label based on property mode.
 */
const propertyHeaderLabel = computed(() => {
  switch (props.propertyMode) {
    case 'purchase':
      return 'Buying property';
    case 'supporting':
    default:
      return 'Property';
  }
});

/**
 * Returns the edit header label based on property mode.
 */
const editPropertyLabel = computed(() => {
  switch (props.propertyMode) {
    case 'purchase':
      return 'Edit Buying property';
    case 'supporting':
    default:
      return 'Edit Existing Property';
  }
});

const propertyTitle = computed(() => {
  const modeLabel = props.propertyMode === 'purchase' ? 'Purchase' : 'Existing';
  const displayAddress = isAddressComplete(props.modelValue.address)
    ? formatAddress(props.modelValue.address)
    : 'the new property';
  return `${modeLabel}: ${displayAddress}`;
});

const editProperty = () => {
  showEntry.value = true;
};

const update = async () => {
  const saved = (await propertyRef.value?.update()) ?? false;

  if (saved) {
    emit('saved');
  }

  showEntry.value = !saved;
  return saved;
};

const cancel = () => {
  if (newItem.value) {
    removeItem();
  } else {
    propertyRef.value?.cancel();
    showEntry.value = false;
  }
};

const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.id);
};

watch(
  () => props.modelValue,
  (val) => {
    inputVal.value = val;
    newItem.value = !val.address;
    showEntry.value = !isAddressComplete(val.address);
  },
  { immediate: true },
);

watch(
  () => props.isMultiple,
  (val) => {
    showEntry.value = !val || newItem.value || !isAddressComplete(props.modelValue.address);
  },
  { immediate: true },
);

if (props.startEditing) showEntry.value = true;

defineExpose({ update });
</script>

<template>
  <div>
    <BxExpander v-model="showEntry" separator>
      <template #header>
        <div class="px-4 py-5 sm:px-6">
          <template v-if="newItem">
            <div class="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                <span class="mr-3 hidden md:block">
                  <BxIcon :name="propertyIcon" />
                </span>
                <span>{{ propertyHeaderLabel }}</span>
              </h3>
            </div>
          </template>
          <template v-else-if="showEntry">
            <h3 class="text-lg font-medium leading-6 text-gray-900">{{ editPropertyLabel }}</h3>
          </template>
          <template v-else>
            <div class="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="ml-4 mt-4">
                <h3 class="flex items-center text-lg font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block">
                    <BxIcon :name="propertyIcon" />
                  </span>
                  <span>{{ propertyTitle }}</span>
                </h3>
              </div>
              <div class="ml-4 mt-4 flex-shrink-0">
                <BxButton variant="secondary" @click="editProperty()">Edit</BxButton>
                <BxButton v-if="isMultiple && removable" variant="link" class="ml-4" @click="showModal = true">
                  Remove
                </BxButton>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template #body>
        <div class="p-6">
          <PropertyDetailForm
            ref="propertyRef"
            :model-value="inputVal"
            :property-mode="propertyMode"
            @update:model-value="emit('update:modelValue', $event)"
          />
          <div v-if="isMultiple" class="mt-10 flex-shrink-0">
            <BxButton :id="inputVal.id + '-save'" variant="secondary" @click="update()">Save</BxButton>
            <BxButton variant="link" class="ml-4" @click="cancel()">Cancel</BxButton>
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      title="Remove property"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ address }}</span
      >?
    </BxModal>
  </div>
</template>
